import Modal from "@/common/components/Modal";
import ResumeItem from "@/common/components/ResumeItem";
import SelectController from "@/common/components/SelectController";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { DateRange } from "react-day-picker";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { dashboardKind } from "../../constants/dashboards/dashboardKind.constant";
import { useUpdateDashboardComponents } from "../../hooks/dashboards/useUpdateDashboardComponent";
import { updateDashboardComponentSchema, UpdateDashboardComponentValidationSchema } from "../../schemas/dashboards/dashboardComponent";
import { DashboardFilters } from "../../types/dashboards/dashboard.type";
import { DashboardComponentData, DashboardComponentDateFilters, DashboardComponentFiltersProp } from "../../types/dashboards/dashboardComponent.type";
import { getDashboardFilterLabel } from "../../utils/dashboards/getDashboardFilterLabel";
import DashboardComponentPreview from "./DashboardComponentPreview";
import UpdateDashboardComponentHiddenFilter from "./UpdateDashboardComponentHiddenFilter";

interface UpdateDashboardComponentProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  dashboardId: string;
  dashboardComponent: DashboardComponentData | undefined;
  filters?: DashboardComponentFiltersProp;
  dashboardDesiredFilters?: DashboardFilters[];
}

const UpdateDashboardComponent = ({
  isOpen,
  setIsOpen,
  dashboardId,
  dashboardComponent,
  filters,
  dashboardDesiredFilters,
}: UpdateDashboardComponentProps) => {
  const queryClient = useQueryClient();

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const {
    control,
    setError,
    setValue,
    handleSubmit,
    reset,
  } = useForm<UpdateDashboardComponentValidationSchema>({
    resolver: zodResolver(updateDashboardComponentSchema),
  });

  const watchFilters = useWatch({
    control,
    name: 'filters',
  });

  const {
    mutate: updateDashboardComponent,
    isPending: updateDashboardComponentIsPending,
  } = useUpdateDashboardComponents({
    dashboardId,
    dashboardComponentId: dashboardComponent?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['dashboard', dashboardId],
        });

        queryClient.invalidateQueries({
          queryKey: ['dashboardComponent', dashboardComponent?.id],
        });

        setIsOpen(false);
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<UpdateDashboardComponentValidationSchema> = (data) => updateDashboardComponent(data);

  const kindOptions = useMemo(() => {
    if (!dashboardComponent) {
      return [];
    }

    return dashboardComponent.definition.available_kinds?.map((kind) => ({
      value: kind,
      label: dashboardKind[kind] || kind,
    }));
  }, [isOpen]);

  const unionFilters = useMemo((): DashboardComponentFiltersProp => {
    const formFilters = watchFilters?.reduce<DashboardComponentFiltersProp>((prev, filter) => {
      if (!filter.value) {
        return prev;
      }

      if (filter.key === DashboardComponentDateFilters.StartDate) {
        return {
          ...prev,
          date: {
            ...prev.date,
            from: filter.value,
          } as DateRange,
        }
      }

      if (filter.key === DashboardComponentDateFilters.EndDate) {
        return {
          ...prev,
          date: {
            ...prev.date,
            to: filter.value,
          } as DateRange,
        }
      }

      return {
        ...prev,
        [filter.key]: filter.value,
      }
    }, { date: undefined });

    return {
      ...filters,
      ...formFilters,
    };
  }, [
    JSON.stringify(filters),
    JSON.stringify(watchFilters),
  ]);

  useEffect(() => {
    reset({
      kind: dashboardComponent?.data?.kind ? {
        label: dashboardKind[dashboardComponent?.data?.kind],
        value: dashboardComponent?.data?.kind,
      } : undefined,
      filters: Object.keys(dashboardComponent?.definition?.filters || {}).map((filterKey) => {
        const generatorPropertiesFilter = dashboardComponent
          ?.generator_properties
          ?.filters
          ?.find(({ key }) => key === filterKey);

        let value: any = generatorPropertiesFilter?.value;

        if (!!generatorPropertiesFilter) {
          if (filterKey === DashboardComponentDateFilters.StartDate || filterKey === DashboardComponentDateFilters.EndDate) {
            value = value ? new Date(value) : undefined;
          } else if (filterKey !== DashboardFilters.FuelProductsOnly) {
            value = {
              value: value,
              label: value,
            }
          }
        }

        return {
          key: filterKey,
          value: value,
          checked: !!generatorPropertiesFilter,
        }
      }),
    });
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Editar gráfico"
      className="rounded-lg overflow-hidden w-[90vw] max-w-[920px] h-[90vh] max-h-[760px] bg-black-900 flex flex-col"
      bodyClassName="text-white-800 flex-1 flex flex-col overflow-hidden"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-200"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={updateDashboardComponentIsPending}
      preventAutoFocus
      body={(
        <form className="h-full flex-1 overflow-hidden flex gap-3">
          <div className="flex-1 flex flex-col overflow-hidden gap-3">
            <DashboardComponentPreview
              generator={dashboardComponent?.definition}
              kind={dashboardComponent?.generator_properties?.kind}
              filters={unionFilters}
              dashboardDesiredFilters={dashboardDesiredFilters}
              ignoreDashboardDesiredFilters
            />

            <SelectController
              control={control}
              name="kind"
              label="Tipo do gráfico"
              markAsRequired
              options={kindOptions}
            />

            <div className="flex flex-col gap-3 overflow-auto thin-scroll">
              <ResumeItem
                className="text-sm"
                label="Título"
                value={dashboardComponent?.definition?.title || '--'}
              />

              {dashboardComponent?.definition?.subtitle && (
                <ResumeItem
                  className="text-sm"
                  label="Descrição"
                  value={dashboardComponent?.definition?.subtitle}
                />
              )}

              {Object.keys(dashboardComponent?.definition?.filters || {}).length > 0 && (
                <ResumeItem
                  className="text-sm"
                  label="Filtros disponíveis"
                  value={
                    '• ' + Object.keys(dashboardComponent?.definition?.filters || {})
                      .map(getDashboardFilterLabel)
                      .filter(Boolean)
                      .join('\n• ')
                  }
                  valueClassName="whitespace-pre-wrap"
                />
              )}
            </div>
          </div>

          <div className="h-full w-px bg-neutral-op-8" />

          <div className="flex-1 flex flex-col gap-3 overflow-hidden">
            <div className="flex flex-col gap-1">
              <p className="font-bold">
                Filtros específicos
              </p>

              <p className="text-sm text-white-300 italic">
                São filtros aplicados somente para este gráfico e sobreescrevem os filtros da tela
              </p>
            </div>

            <div className="flex flex-col gap-3 overflow-auto thin-scroll">
              {Object.keys(dashboardComponent?.definition?.filters || {}).length > 0 && (
                watchFilters.map((filter, index) => (
                  <UpdateDashboardComponentHiddenFilter
                    key={filter.key}
                    filter={filter}
                    index={index}
                    setValue={setValue}
                  />
                ))
              )}
            </div>
          </div>
        </form>
      )}
    />
  );
}

export default UpdateDashboardComponent;
